import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaUserPlus } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import DonutChart from "Components/DonutChart";
import SurveyDetailModal from "Components/SurveyDetailModal";
import { catchError } from "JS/common";
import { getSignupStats, getSurveyStats, getSurveyDetail, getSurveyRevenueStats } from "JS/API";
import UtmStatsTab from "Components/UtmStatsTab";
import { buildCsv, downloadCsvFile } from "utils/csv";
import UtmLinkBuilderTab from "Components/UtmLinkBuilderTab";
import SegmentControl from "Components/Sales/SegmentControl";
import SurveyRevenueView from "Components/SurveyRevenueView";

const DETAIL_LIMIT = 10;

// 응답이 { meta, data } 로 감싸진 경우 안쪽 data 를 반환
const unwrap = (res) => res?.data?.data ?? res?.data;

const CSV_HEADERS = [
  "이름",
  "아이디",
  "이메일",
  "유입경로",
  "사용자유형",
  "관심서비스",
  "가입일",
];

const buildDetailCsv = (items) =>
  buildCsv(
    CSV_HEADERS,
    items.map((i) => [i.user_name, i.user_id, i.email, i.inflow, i.user_type, i.interest, i.created_at])
  );

const TABS = [
  { key: "survey", label: "가입·설문" },
  { key: "utm", label: "UTM 유입" },
  { key: "links", label: "UTM 링크 만들기" },
];

const PERIOD_MODES = [
  { key: "month", label: "월별" },
  { key: "all", label: "전체" },
  { key: "custom", label: "기간 설정" },
];

const SURVEY_SECTIONS = [
  { key: "inflow", label: "유입 경로" },
  { key: "user_type", label: "사용자 유형" },
  { key: "interest", label: "관심 서비스" },
];

const METRIC_OPTIONS = [
  { key: "count", label: "인원" },
  { key: "gross", label: "매출" },
  { key: "net", label: "순매출" },
];

const styles = {
  container: {
    padding: "32px 40px",
    backgroundColor: "#f1f5f9",
    minHeight: "100vh",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1f2937",
    margin: "0 0 24px 0",
  },
  tabRow: {
    display: "flex",
    gap: "4px",
    marginBottom: "20px",
    borderBottom: "2px solid #e5e7eb",
  },
  tabBtn: {
    padding: "10px 20px",
    fontSize: "15px",
    fontWeight: "600",
    border: "none",
    backgroundColor: "transparent",
    color: "#6b7280",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    marginBottom: "-2px",
  },
  tabBtnActive: {
    color: "#2563eb",
    borderBottom: "2px solid #2563eb",
  },
  filterCard: {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "20px 24px",
    marginBottom: "28px",
    backgroundColor: "#ffffff",
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  segment: {
    display: "inline-flex",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    overflow: "hidden",
  },
  segmentBtn: {
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "500",
    border: "none",
    backgroundColor: "#ffffff",
    color: "#6b7280",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  segmentBtnActive: {
    backgroundColor: "#2563eb",
    color: "#ffffff",
  },
  monthNav: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  monthLabel: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
    minWidth: "120px",
    textAlign: "center",
  },
  navButton: {
    width: "28px",
    height: "28px",
    backgroundColor: "#f3f4f6",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
  },
  dateInput: {
    padding: "8px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1f2937",
  },
  dateSep: {
    color: "#9ca3af",
  },
  resultArea: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  surveyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(460px, 1fr))",
    gap: "24px",
  },
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "28px",
    backgroundColor: "#ffffff",
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "20px",
  },
  signupCard: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "28px",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
  },
  signupHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "15px",
    color: "#6b7280",
  },
  signupValue: {
    fontSize: "44px",
    fontWeight: "700",
    color: "#1f2937",
    lineHeight: 1.1,
  },
  breakdown: {
    display: "flex",
    gap: "24px",
    marginTop: "16px",
    flexWrap: "wrap",
  },
  breakdownItem: {
    fontSize: "14px",
    color: "#4b5563",
  },
  sectionLabel: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "20px",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "80px",
    color: "#9ca3af",
    fontSize: "14px",
  },
  metricGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginLeft: "auto",
  },
  metricLabel: {
    fontSize: "13px",
    color: "#6b7280",
  },
};

const Statistics = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const activeTab = TABS.some((tab) => tab.key === tabParam)
    ? tabParam
    : TABS[0].key;

  const periodParam = searchParams.get("period");
  const periodMode = PERIOD_MODES.some((m) => m.key === periodParam)
    ? periodParam
    : "month";

  const yearParam = parseInt(searchParams.get("year"), 10);
  const year =
    yearParam >= 2000 && yearParam <= 2100
      ? yearParam
      : new Date().getFullYear();

  const monthParam = parseInt(searchParams.get("month"), 10);
  const month =
    monthParam >= 1 && monthParam <= 12 ? monthParam : new Date().getMonth() + 1;

  const startDate = searchParams.get("start") || "";
  const endDate = searchParams.get("end") || "";

  const metricParam = searchParams.get("metric");
  const metric = METRIC_OPTIONS.some((m) => m.key === metricParam)
    ? metricParam
    : "count";
  const isRevenue = metric !== "count";

  const updateParams = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const changeTab = useCallback(
    (key) =>
      updateParams({
        tab: key,
        period: null,
        year: null,
        month: null,
        start: null,
        end: null,
        page: null,
        metric: null,
      }),
    [updateParams]
  );

  const changePeriodMode = useCallback(
    (mode) => {
      if (mode === "month") {
        updateParams({ period: mode, start: null, end: null, page: null });
      } else if (mode === "all") {
        updateParams({
          period: mode,
          year: null,
          month: null,
          start: null,
          end: null,
          page: null,
        });
      } else {
        updateParams({ period: mode, year: null, month: null, page: null });
      }
    },
    [updateParams]
  );

  const [signupData, setSignupData] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [revenueFailed, setRevenueFailed] = useState(false);
  const revenueRequestRef = useRef(0);

  const [detailTarget, setDetailTarget] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);
  const [detailDownloading, setDetailDownloading] = useState(false);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  // 유효한 조회 조건이 갖춰졌을 때만 filter 생성 (custom 은 양쪽 날짜 필요)
  const filter = useMemo(() => {
    if (periodMode === "month") {
      return { period: "month", year, month };
    }
    if (periodMode === "all") {
      return { period: "all" };
    }
    if (periodMode === "custom" && startDate && endDate) {
      return { period: "custom", startDate, endDate };
    }
    return null;
  }, [periodMode, year, month, startDate, endDate]);

  const loadStats = useCallback(async () => {
    if (!filter) return;
    setIsLoading(true);

    try {
      const [signupRes, surveyRes] = await Promise.all([
        getSignupStats(filter),
        getSurveyStats(filter),
      ]);

      if (typeof signupRes !== "object" || !signupRes?.data) {
        return catchError(signupRes, navigate, setAlertBox, setAlert);
      }
      if (typeof surveyRes !== "object" || !surveyRes?.data) {
        return catchError(surveyRes, navigate, setAlertBox, setAlert);
      }

      setSignupData(unwrap(signupRes));
      setSurveyData(unwrap(surveyRes));
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [filter, navigate]);

  const loadRevenue = useCallback(async () => {
    if (!filter) return;
    const requestId = ++revenueRequestRef.current;
    setRevenueData(null);
    setRevenueFailed(false);
    setRevenueLoading(true);

    try {
      const res = await getSurveyRevenueStats(filter);
      if (requestId !== revenueRequestRef.current) return;
      if (typeof res !== "object" || !res?.data) {
        setRevenueFailed(true);
        return catchError(res, navigate, setAlertBox, setAlert);
      }
      setRevenueData(unwrap(res));
    } catch (error) {
      if (requestId !== revenueRequestRef.current) return;
      setRevenueFailed(true);
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      if (requestId === revenueRequestRef.current) setRevenueLoading(false);
    }
  }, [filter, navigate]);

  const handlePrevMonth = useCallback(() => {
    if (month === 1) {
      updateParams({ year: year - 1, month: 12, page: null });
    } else {
      updateParams({ year, month: month - 1, page: null });
    }
  }, [year, month, updateParams]);

  const handleNextMonth = useCallback(() => {
    if (month === 12) {
      updateParams({ year: year + 1, month: 1, page: null });
    } else {
      updateParams({ year, month: month + 1, page: null });
    }
  }, [year, month, updateParams]);

  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  const handleApiError = useCallback(
    (error) => catchError(error, navigate, setAlertBox, setAlert),
    [navigate]
  );

  const fetchDetailPage = useCallback(
    async (target, page, limit = DETAIL_LIMIT) => {
      const res = await getSurveyDetail(target.type, target.value, filter, {
        page,
        limit,
      });
      if (typeof res !== "object" || !res?.data) {
        catchError(res, navigate, setAlertBox, setAlert);
        return null;
      }
      return unwrap(res);
    },
    [filter, navigate]
  );

  const openDetail = useCallback(
    async (section, segment) => {
      if (!filter) return;
      const target = {
        type: section.key,
        typeLabel: section.label,
        value: segment.label,
        count: segment.count,
        percent: segment.percent,
        color: segment.color,
      };
      setDetailTarget(target);
      setDetailPage(1);
      setDetailData(null);
      setDetailLoading(true);
      const data = await fetchDetailPage(target, 1);
      if (data) setDetailData(data);
      setDetailLoading(false);
    },
    [filter, fetchDetailPage]
  );

  const changeDetailPage = useCallback(
    async (page) => {
      if (!detailTarget) return;
      setDetailPage(page);
      setDetailLoading(true);
      const data = await fetchDetailPage(detailTarget, page);
      if (data) setDetailData(data);
      setDetailLoading(false);
    },
    [detailTarget, fetchDetailPage]
  );

  const downloadDetailCsv = useCallback(async () => {
    if (!detailTarget) return;
    setDetailDownloading(true);
    try {
      const total = detailData?.total || detailTarget.count || 0;
      const all = await fetchDetailPage(detailTarget, 1, total || 1);
      if (!all?.items?.length) return;
      const csv = buildDetailCsv(all.items);
      downloadCsvFile(csv, `통계_${detailTarget.typeLabel}_${detailTarget.value}.csv`);
    } finally {
      setDetailDownloading(false);
    }
  }, [detailTarget, detailData, fetchDetailPage]);

  const closeDetail = useCallback(() => {
    setDetailTarget(null);
    setDetailData(null);
    setDetailPage(1);
  }, []);

  useEffect(() => {
    document.title = "마크픽 관리자 > 통계";
  }, []);

  useEffect(() => {
    if (activeTab === "survey" && !isRevenue) loadStats();
  }, [activeTab, isRevenue, loadStats]);

  useEffect(() => {
    if (activeTab === "survey" && isRevenue) loadRevenue();
  }, [activeTab, isRevenue, loadRevenue]);

  const periodLabel = useMemo(() => {
    if (periodMode === "month") return `${year}년 ${month}월`;
    if (periodMode === "all") return "전체 기간";
    if (startDate && endDate) return `${startDate} ~ ${endDate}`;
    return "기간을 선택해 주세요";
  }, [periodMode, year, month, startDate, endDate]);

  const renderResult = () => {
    if (periodMode === "custom" && (!startDate || !endDate)) {
      return <div style={styles.loading}>시작일과 종료일을 선택해 주세요.</div>;
    }
    if (isRevenue) {
      if (revenueFailed) {
        return <div style={styles.loading}>데이터를 불러오지 못했습니다.</div>;
      }
      if (revenueLoading || !revenueData) {
        return <div style={styles.loading}>로딩 중...</div>;
      }
      return (
        <SurveyRevenueView
          data={revenueData}
          metric={metric}
          periodLabel={periodLabel}
          sections={SURVEY_SECTIONS}
        />
      );
    }
    if (isLoading) {
      return <div style={styles.loading}>로딩 중...</div>;
    }

    const total = signupData?.total || 0;
    const breakdown = signupData?.breakdown || [];

    return (
      <div style={styles.resultArea}>
        <div style={styles.signupCard}>
          <div style={styles.signupHeader}>
            <FaUserPlus size={14} />
            {periodLabel} 가입자 수
          </div>
          <div style={styles.signupValue}>{total.toLocaleString()}명</div>
          {breakdown.length > 0 && (
            <div style={styles.breakdown}>
              {breakdown.map((item) => (
                <span key={item.label} style={styles.breakdownItem}>
                  {item.label} <strong>{item.count.toLocaleString()}명</strong>
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={styles.surveyGrid}>
          {SURVEY_SECTIONS.map((section) => (
            <div key={section.key} style={styles.card}>
              <div style={styles.cardTitle}>{section.label}</div>
              <DonutChart
                data={surveyData?.[section.key]?.items || []}
                onSelect={(segment) => openDetail(section, segment)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>통계</h1>

      <div style={styles.tabRow}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            style={{
              ...styles.tabBtn,
              ...(activeTab === tab.key ? styles.tabBtnActive : {}),
            }}
            onClick={() => changeTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 기간 필터 (링크 생성 탭은 기간 개념이 없음) */}
      {activeTab !== "links" && (
      <div style={styles.filterCard}>
        <div style={styles.filterRow}>
          <div style={styles.segment}>
            {PERIOD_MODES.map((m) => (
              <button
                key={m.key}
                style={{
                  ...styles.segmentBtn,
                  ...(periodMode === m.key ? styles.segmentBtnActive : {}),
                }}
                onClick={() => changePeriodMode(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>

          {periodMode === "month" && (
            <div style={styles.monthNav}>
              <button style={styles.navButton} onClick={handlePrevMonth} aria-label="이전 달">
                <FaChevronLeft size={12} />
              </button>
              <span style={styles.monthLabel}>
                {year}년 {month}월
              </span>
              <button style={styles.navButton} onClick={handleNextMonth} aria-label="다음 달">
                <FaChevronRight size={12} />
              </button>
            </div>
          )}

          {periodMode === "custom" && (
            <div style={styles.monthNav}>
              <input
                type="date"
                style={styles.dateInput}
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => updateParams({ start: e.target.value, page: null })}
              />
              <span style={styles.dateSep}>~</span>
              <input
                type="date"
                style={styles.dateInput}
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => updateParams({ end: e.target.value, page: null })}
              />
            </div>
          )}

          {activeTab === "survey" && (
            <div style={styles.metricGroup}>
              <span style={styles.metricLabel}>지표</span>
              <SegmentControl
                options={METRIC_OPTIONS}
                value={metric}
                onChange={(key) => updateParams({ metric: key === "count" ? null : key })}
              />
            </div>
          )}
        </div>
      </div>
      )}

      {/* 결과 */}
      {activeTab === "links" ? (
        <UtmLinkBuilderTab onError={handleApiError} />
      ) : activeTab === "survey" ? (
        renderResult()
      ) : periodMode === "custom" && (!startDate || !endDate) ? (
        <div style={styles.loading}>시작일과 종료일을 선택해 주세요.</div>
      ) : (
        <UtmStatsTab
          filter={filter}
          periodLabel={periodLabel}
          onError={handleApiError}
        />
      )}

      {detailTarget && (
        <SurveyDetailModal
          type={detailTarget.type}
          typeLabel={detailTarget.typeLabel}
          value={detailTarget.value}
          color={detailTarget.color}
          total={detailTarget.count}
          percent={detailTarget.percent}
          periodLabel={periodLabel}
          detail={detailData}
          isLoading={detailLoading}
          page={detailPage}
          limit={DETAIL_LIMIT}
          onPageChange={changeDetailPage}
          onDownloadCsv={downloadDetailCsv}
          downloading={detailDownloading}
          onClose={closeDetail}
        />
      )}

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={handleAlertOk}
        />
      )}
    </div>
  );
};

export default Statistics;
